import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { first } from 'lodash';

import { injectIntl } from 'react-intl';

import { Row, Col } from 'react-bootstrap';
import { Modal, Select } from 'sa-ui';

import messages from './addClassModalMessages';

function AddClassModal({
  intl,
  show,
  schools,
  onClose,
  onManualRegister,
  onContinue,
  massiveGroup,
  reset,
  getGroups,
  getSchools,
  warnings,
  error,
  canEditSchool,
  hasAccessToDemoSchool,
}) {
  const [file, setFile] = useState(null);
  const [school, setSchool] = useState(0);
  const onDrop = useCallback((acceptedFiles) => {
    setFile(first(acceptedFiles));
  }, []);
  const { getRootProps } = useDropzone({ onDrop });

  const data = [
    { value: 0, label: intl.formatMessage(messages.selectSchool) },
    ...schools
      .filter(
        (elem) => canEditSchool || (hasAccessToDemoSchool && elem.idCSchool == 406)
      )
      .map((elem) => ({
        ...elem,
        value: elem.idCSchool,
        label: elem.name
      }))
  ];

  React.useEffect(() => {
    if (schools.length === 1) {
      setSchool(schools[0].idCSchool);
    }
  }, [schools]);

  function handleOnClose() {
    setFile(null);
    onClose();
  }

  if (massiveGroup.success) {
    return (
      <Modal
        show={show}
        onClose={() => {
          setFile(null);
          getGroups();
          getSchools();
          setTimeout(() => {
            onClose();
          }, 1000);
        }}
        icon="ti-thumb-up"
        title={intl.formatMessage(messages.feedbackTitle)}
        buttons={[
          {
            label: intl.formatMessage(messages.continue),
            onClick: () => {
              setFile(null);
              getGroups();
              getSchools();
              onClose();
            },
            primary: true,
          },
        ]}
      >
        <p>{intl.formatMessage(messages.feedbackMessage, { file: file.name })}</p>

        <b>{intl.formatMessage(messages.warning)}:</b>
        <div style={{ textAlign: 'left' }}>
          {warnings && warnings.map((el) => <div style={{ marginBottom: '10px' }}>{el}</div>)}
          {error && <div style={{ marginBottom: '10px' }}>{error}</div>}
        </div>
      </Modal>
    );
  }

  if (massiveGroup.hasError) {
    const errorType1 = 'This field can not be empty';
    const errorType2 = 'Segment or level does not found';
    const errorType3 = 'Invalid period for this course';
    const errorType4 = 'E-mail is not valid';
    const errorType5 = 'This email is already registered by another non-student user';
    const errorType6 = 'E-mail can not be empty';

    const commonErrors1 = 'Empty file, please fill the blanks needed.';
    const commonErrors2 = 'This user has no permission for this idCSchool';
    const commonErrors3 = 'File type not supported. Only .xls or .xlsx files can be uploaded.';

    return (
      <Modal
        show={show}
        onClose={onClose}
        icon="ti-thumb-down"
        title={intl.formatMessage(messages.feedbackErrorTitle)}
        subtitle={intl.formatMessage(messages.errorFeedbackMessage)}
        buttons={[
          {
            label: intl.formatMessage(messages.continue),
            onClick: () => reset(),
            primary: true,
          },
        ]}
        fullscreen
      >
        {massiveGroup.errors &&
          massiveGroup.errors.map((e) => {
            let errorMessage = '';
            if (errorType1 === e.error) errorMessage = intl.formatMessage(messages.errorType1);
            if (errorType2 === e.error) errorMessage = intl.formatMessage(messages.errorType2);
            if (errorType3 === e.error) errorMessage = intl.formatMessage(messages.errorType3);
            if (errorType4 === e.error) errorMessage = intl.formatMessage(messages.errorType4);
            if (errorType5 === e.error) errorMessage = intl.formatMessage(messages.errorType5);
            if (errorType6 === e.error) errorMessage = intl.formatMessage(messages.errorType6);

            return (
              <p style={{ color: '#dc3545' }}>
                {`${intl.formatMessage(messages.line)}: ${e.row}, ${intl.formatMessage(
                  messages.column,
                )}: ${e.column}, ${errorMessage}`}
              </p>
            );
          })}
        {massiveGroup.error && massiveGroup.error === commonErrors1 && (
          <p style={{ color: '#dc3545' }}>{intl.formatMessage(messages.commonErrors1)}</p>
        )}
        {massiveGroup.error && massiveGroup.error === commonErrors2 && (
          <p style={{ color: '#dc3545' }}>{intl.formatMessage(messages.commonErrors2)}</p>
        )}
        {massiveGroup.error && massiveGroup.error === commonErrors3 && (
          <p style={{ color: '#dc3545' }}>{intl.formatMessage(messages.commonErrors3)}</p>
        )}
        {massiveGroup.error && massiveGroup.error !== "" && (
          <p style={{ color: '#dc3545' }}>{massiveGroup.error}</p>
        )}
        <p>{intl.formatMessage(messages.errorFeedbackInstructions)}</p>
      </Modal>
    );
  }

  const isDisabled = !file || school === 0;

  return (
    <Modal
      show={show}
      onClose={() => handleOnClose()}
      icon="ti-thumb-up"
      title={intl.formatMessage(messages.title)}
      subtitle={intl.formatMessage(messages.subtitle)}
      buttons={[
        {
          label: intl.formatMessage(messages.manual),
          onClick: onManualRegister,
        },
        {
          label: intl.formatMessage(messages.continue),
          onClick: () => onContinue({ file, school }),
          primary: true,
          disabled: isDisabled,
          style: { cursor: isDisabled ? 'not-allowed' : 'pointer' },
        },
      ]}
    >
      <p style={{ textAlign: 'justify' }}>
        {intl.formatMessage(messages.firstSection)}
        <br />
        {intl.formatMessage(messages.registrationInfo)}
      </p>
      <a
        className="btn btn-outline-primary"
        href="/static/TEMPLATE-LISTA_v2.xlsx"
        download="TEMPLATE-LISTA_v2.xlsx"
      >
        {intl.formatMessage(messages.downloadButton)}
      </a>
      <br />
      <span style={{ fontSize: '12px' }}>{intl.formatMessage(messages.updatedMonth)}</span>
      <p className="mt-5 mb-5">{intl.formatMessage(messages.secondSection)}</p>
      <div {...getRootProps()}>
        <i className="icon icon-small ti-cloud-up" />

        <h6 className="subtitle subtitle-bigger mt-2">
          {file && (
            <span>
              {file.name}
              <br />
            </span>
          )}
          {intl.formatMessage(messages.dragDrop)}
          <br />
          <span>
            <input
              type="file"
              id="file"
              name="file"
              accept="*"
              style={{ display: 'none' }}
              onChange={(e) => {
                setFile(first(e.target.files));
              }}
            />
            <a
              onClick={() => {
                const fileBtn = document.getElementById('file').click();
              }}
            >
              {intl.formatMessage(messages.clickHere)}
            </a>
          </span>
        </h6>
      </div>
      {schools.length !== 1 && (
        <div className="align-center">
          <Select
            onChange={(e) => setSchool(e.target.value)}
            value={school}
            options={data}
            style={{ maxWidth: '400px' }}
          />
        </div>
      )}
      {massiveGroup.hasError && (
        <div className="mt-2 align-center" style={{ color: '#ec745d' }}>
          Não foi possível salvar
        </div>
      )}
    </Modal>
  );
}

export default injectIntl(AddClassModal);
